import { useRef, useState } from 'react';
import type { BaggageSegment, FlightSegment } from '../../types';
import { fromMinor, money, toMinor } from '../../core';
import { newId, useActions, useStore } from '../../store';
import { TextField, MoneyField, SelectField } from './fields';
import formShell from './formShell.module.css';
import { flightShortDateLabel } from './flightLabel';
import { useT } from '../../i18n';

export interface BaggageFormProps {
  mode: 'create' | 'edit';
  segment?: BaggageSegment;
  onClose(): void;
}

export function BaggageForm({ mode, segment, onClose }: BaggageFormProps) {
  const t = useT();
  const state = useStore();
  const actions = useActions();

  const flights = Object.values(state.library).filter(
    (s): s is FlightSegment => s.kind === 'flight',
  );

  const [flightId, setFlightId] = useState(segment?.flightId ?? '');
  const [label, setLabel] = useState(segment?.label ?? '');
  const [amount, setAmount] = useState(segment ? String(fromMinor(segment.price.amount)) : '');
  const [currency, setCurrency] = useState(segment?.price.currency ?? 'USD');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const refs = useRef<Record<string, HTMLElement | null>>({});

  function validate(): Record<string, string> {
    const errs: Record<string, string> = {};
    if (!flightId) errs.flightId = t('error.flightRequired');
    if (!label.trim()) errs.label = t('error.labelRequired');
    const amountNum = parseFloat(amount.replace(',', '.'));
    if (amount.trim() === '' || Number.isNaN(amountNum) || amountNum < 0) {
      errs.amount = t('error.priceNegative');
    }
    return errs;
  }

  function focusFirstError(errs: Record<string, string>) {
    for (const key of ['flightId', 'label', 'amount']) {
      if (errs[key]) {
        refs.current[key]?.focus();
        return;
      }
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      focusFirstError(errs);
      return;
    }
    const built: BaggageSegment = {
      kind: 'baggage',
      id: segment?.id ?? newId('b'),
      flightId,
      label: label.trim(),
      price: money(toMinor(amount), currency),
    };
    if (mode === 'edit' && segment) {
      actions.updateSegment(built.id, built);
    } else {
      actions.addSegment(built);
    }
    onClose();
  }

  return (
    <form onSubmit={handleSubmit}>
      {flights.length === 0 ? (
        <p className="field-error" role="alert">
          {t('baggage.noFlights')}
        </p>
      ) : (
        <SelectField
          id="baggage-flight"
          label={t('field.flight')}
          value={flightId}
          onChange={setFlightId}
          placeholder={t('form.pickFlight')}
          required
          autoFocus
          error={errors.flightId}
          options={flights.map((f) => ({ value: f.id, label: flightShortDateLabel(f) }))}
          selectRef={(el) => {
            refs.current.flightId = el;
          }}
        />
      )}
      <TextField
        id="baggage-label"
        label={t('field.label')}
        value={label}
        onChange={setLabel}
        required
        autoFocus={flights.length === 0}
        error={errors.label}
        inputRef={(el) => {
          refs.current.label = el;
        }}
      />
      <MoneyField
        idPrefix="baggage-price"
        label={t('field.price')}
        amount={amount}
        onAmountChange={setAmount}
        currency={currency}
        onCurrencyChange={setCurrency}
        required
        error={errors.amount}
        inputRef={(el) => {
          refs.current.amount = el;
        }}
      />
      <div className={formShell.footer}>
        <button type="button" className="btn" onClick={onClose}>
          {t('form.cancel')}
        </button>
        <button type="submit" className="btn btn-primary">
          {t('form.save')}
        </button>
      </div>
    </form>
  );
}
